import logger from "../../../utils/logger";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchRequestPayments,
  fetchRequestPaymentsByCompany,
} from "../../../slices/apps/orderSlice";
import { fetchServiceCommissionSummary } from "../../../slices/apps/companySlice";

// excel import
import ExcelJS from "exceljs";

// Sayfalar, modallar vb.
import DebouncedInput from "../../../Components/Common/DebouncedInput";
import TablePagination from "../../../Components/Common/TablePagination";
import RequestPaymentConfirmModal from "./RequestPaymentConfirmModal";

// moment
import moment from "moment";
import "moment/locale/tr";
moment.locale("tr");

// Column Definitions
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

const SubTableComponent = ({ orders }) => {
  const subColumns = useMemo(
    () => [
      columnHelper.accessor("orderNo", {
        header: "Servis No",
        size: 92,
        minSize: 92,
        maxSize: 92,
        cell: (info) => (
          <div className="text-truncate fs-12">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("customerName", {
        header: "Müşteri",
        size: "auto",
        minSize: 150,
        maxSize: 250,
        cell: (info) => (
          <div className="text-truncate fs-12">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("commissionFee", {
        header: "Komisyon",
        size: 155,
        minSize: 155,
        maxSize: 155,
        cell: (info) => (
          <div className="fs-12">
            {currencyFormatter.format(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor("totalAmount", {
        header: "Tutar",
        size: 155,
        minSize: 155,
        maxSize: 155,
        cell: (info) => (
          <div className="fs-12">
            {currencyFormatter.format(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor("creationDate", {
        header: "Oluşturma Tarihi",
        size: 155,
        minSize: 155,
        maxSize: 155,
        cell: (info) => (
          <div className="text-truncate fs-12">
            {new Date(info.getValue()).toLocaleString("tr-TR")}
          </div>
        ),
      }),
    ],
    []
  );

  const subTable = useReactTable({
    data: orders,
    columns: subColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Table size="sm">
      <thead className="table-light">
        {subTable.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="text-muted"
                style={{
                  width: header.column.columnDef.size,
                  minWidth: header.column.columnDef.minSize,
                  maxWidth: header.column.columnDef.maxSize,
                  letterSpacing: "0.8px",
                }}
              >
                {header.isPlaceholder ? null : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                      userSelect: "none",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: <i className="ri-arrow-down-s-line"></i>,
                      desc: <i className="ri-arrow-up-s-line"></i>,
                    }[header.column.getIsSorted()] ?? null}
                  </div>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {orders.length === 0 ? (
          <tr>
            <td
              colSpan={subColumns.length}
              className="text-center text-muted fs-12"
            >
              Servis bulunamadı
            </td>
          </tr>
        ) : (
          subTable.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{
                    width: cell.column.columnDef.size,
                    minWidth: cell.column.columnDef.minSize,
                    maxWidth: cell.column.columnDef.maxSize,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
};

const RequestPaymentTable = () => {
  const dispatch = useDispatch();

  const { requestPayments, requestPaymentsLoading, isUserData } = useSelector(
    (state) => {
      return {
        requestPayments: state.order.requestPayments,
        requestPaymentsLoading: state.order.requestPaymentsLoading,
        isUserData: state.auth.isUserData,
      };
    },
    shallowEqual
  );

  // States
  const [data, setData] = useState([]);
  const [isTableMode, setIsTableMode] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    try {
      if (isTableMode) {
        await dispatch(fetchRequestPaymentsByCompany()).unwrap();
      } else {
        await dispatch(fetchRequestPayments()).unwrap();
      }
    } catch (error) {
      logger.error("Veri çekme hatası:", error);
    }
  };

  const handleRefresh = async () => {
    await fetchData();
    await dispatch(fetchServiceCommissionSummary()).unwrap();
  };

  useEffect(() => {
    fetchData();
  }, [isTableMode]);

  useEffect(() => {
    if (!requestPayments) {
      setData([]);
      return;
    }

    const filteredData = requestPayments?.filter((data) => {
      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          data.payReqId,
          data.earnedCompanyName,
          data.totalAmount,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }

      return true;
    });

    setData(filteredData || []);
  }, [globalFilter, requestPayments]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("expander", {
        header: () => null,
        id: "expander",
        size: 46,
        minSize: 46,
        maxSize: 46,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <button
              {...{
                onClick: row.getToggleExpandedHandler(),
                style: {
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                },
              }}
            >
              {row.getIsExpanded() ? (
                <i className="ri-arrow-down-s-line fs-18"></i>
              ) : (
                <i className="ri-arrow-right-s-line fs-18"></i>
              )}
            </button>
          ) : null,
      }),

      ...(!isTableMode
        ? [
            columnHelper.display({
              id: "actions",
              header: () => null,
              size: 46,
              minSize: 46,
              maxSize: 46,
              enableResizing: false,
              cell: ({ row }) => (
                <div className="d-flex gap-3">
                  {!row.original.isPaid && isUserData?.role == "Admin" ? (
                    <RequestPaymentConfirmModal id={row.original.payReqId} />
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        padding: "3px",
                      }}
                    >
                      <i className="ri-money-dollar-circle-line fs-18 text-muted" />
                    </span>
                  )}
                </div>
              ),
            }),
          ]
        : []),

      columnHelper.accessor("earnedCompanyName", {
        header: "Firma Adı",
        size: "auto",
        minSize: 150,
        maxSize: 250,
        cell: ({ row }) => (
          <div className="d-flex flex-column">
            <div className="text-truncate fw-bold fs-12">
              {row.original.earnedCompanyName}
            </div>
            <div className="text-truncate fw-medium fs-12">
              {row.original.description || "-"}
            </div>
          </div>
        ),
      }),

      columnHelper.accessor("isPaid", {
        header: "Durum",
        size: 125,
        minSize: 125,
        maxSize: 125,
        enableResizing: false,

        cell: ({ row }) => (
          <div>
            {isTableMode ? (
              <div className="badge bg-warning-subtle text-warning">
                Talep Bekliyor
              </div>
            ) : row.original.isPaid ? (
              <div className="badge bg-success-subtle text-success">Ödendi</div>
            ) : (
              <div className="badge bg-warning-subtle text-warning">
                Ödeme Bekliyor
              </div>
            )}
          </div>
        ),
      }),

      columnHelper.accessor("isPayReqDate", {
        header: "Talep Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.isPayReqDate
              ? moment(row.original.isPayReqDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("invoiceNo", {
        header: "Fatura No",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.invoiceNo ? row.original.invoiceNo : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("invoiceDate", {
        header: "Fatura Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.invoiceDate
              ? moment(row.original.invoiceDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("totalCommissionFee", {
        header: "Toplam Komisyon",
        size: 160,
        minSize: 160,
        maxSize: 160,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.totalCommissionFee)}
          </div>
        ),
      }),

      columnHelper.accessor("totalAmount", {
        header: "Servis Tutarı",
        size: 160,
        minSize: 160,
        maxSize: 160,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.totalAmount)}
          </div>
        ),
      }),

      columnHelper.accessor("isPaidDate", {
        header: "Ödeme Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.isPaidDate
              ? moment(row.original.isPaidDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),
    ],
    [isTableMode]
  );

  // Hooks
  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
      expanded,
    },
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  // ** EXCEL EXPORT FUNCTION START **
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Ciro Primi Listesi");

    worksheet.columns = [
      { header: "Firma Adı", key: "EarnedCompanyName", width: 30 },
      { header: "Durum", key: "Status", width: 20 },
      { header: "Talep Tarihi", key: "RequestDate", width: 15 },
      { header: "Fatura No", key: "InvoiceNo", width: 15 },
      { header: "Fatura Tarihi", key: "InvoiceDate", width: 15 },
      { header: "Toplam Komisyon", key: "TotalCommissionFee", width: 20 },
      { header: "Servis Tutarı", key: "TotalAmount", width: 20 },
      { header: "Ödeme Tarihi", key: "PaidDate", width: 15 },
    ];

    data.forEach((row) => {
      // Durum Mantığı
      let statusText = "";
      if (isTableMode) {
        statusText = "Talep Bekliyor";
      } else if (row.isPaid) {
        statusText = "Ödendi";
      } else {
        statusText = "Ödeme Bekliyor";
      }

      // Tarih Formatları
      const requestDate = row.isPayReqDate
        ? moment(row.isPayReqDate).format("DD.MM.YYYY")
        : "-";
      const invoiceDate = row.invoiceDate
        ? moment(row.invoiceDate).format("DD.MM.YYYY")
        : "-";
      const paidDate = row.isPaidDate
        ? moment(row.isPaidDate).format("DD.MM.YYYY")
        : "-";

      // Para Formatları
      const commission = row.totalCommissionFee
        ? currencyFormatter.format(row.totalCommissionFee)
        : currencyFormatter.format(0);
      const amount = row.totalAmount
        ? currencyFormatter.format(row.totalAmount)
        : currencyFormatter.format(0);

      worksheet.addRow({
        EarnedCompanyName: row.earnedCompanyName || "-",
        Status: statusText,
        RequestDate: requestDate,
        InvoiceNo: row.invoiceNo || "-",
        InvoiceDate: invoiceDate,
        TotalCommissionFee: commission,
        TotalAmount: amount,
        PaidDate: paidDate,
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const dateStr = new Date().toISOString().split("T")[0];
    const fileName = `Ciro_Primi_Listesi_${dateStr}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // ** EXCEL EXPORT FUNCTION END **

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  const toggleDataMode = () => {
    setIsTableMode((prevMode) => !prevMode);
  };

  return (
    <Fragment>
      <Card>
        <CardBody className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-label btn"
            color="warning"
            type="button"
            onClick={() => handleRefresh()}
          >
            <i className="ri-refresh-line label-icon align-middle me-2"></i>
            Yenile
          </Button>

          <Button
            className={`btn btn-label btn ${
              isTableMode ? "btn-primary" : "btn-primary"
            }`}
            color={isTableMode ? "primary" : "primary"}
            type="button"
            onClick={() => toggleDataMode()}
          >
            <i className="ri-exchange-line label-icon align-middle me-2"></i>
            {isTableMode
              ? "Talep Edilenleri Göster"
              : "Talep Edilmeyenleri Göster"}
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fs-14">
            Ciro Primi Listesi
          </h4>
          <div className="flex-shrink-0">
            {isUserData?.role === "Admin" && (
              <UncontrolledDropdown className="card-header-dropdown">
                <DropdownToggle
                  tag="a"
                  className="text-reset"
                  role="button"
                  caret
                >
                  İşlemler
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem onClick={() => exportToExcel()}>
                    <i className="ri-file-excel-2-line label-icon align-middle me-2"></i>
                    Excel İndir
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="Sayfa başına kayıt sayısı"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(String(value))}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
            </CardBody>
          </Row>

          <div style={{ overflowX: "auto" }}>
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: header.column.getCanSort()
                                ? "pointer"
                                : "default",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {requestPaymentsLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => {
                    return (
                      <Fragment key={row.id}>
                        <tr>
                          {row.getVisibleCells().map((cell) => {
                            return (
                              <td
                                key={cell.id}
                                style={{
                                  width: cell.column.columnDef.size,
                                  minWidth: cell.column.columnDef.minSize,
                                  maxWidth: cell.column.columnDef.maxSize,
                                }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        {row.getIsExpanded() && (
                          <tr>
                            <td colSpan={row.getVisibleCells().length}>
                              <SubTableComponent
                                orders={row.original.orders || []}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination table={table} />
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default RequestPaymentTable;
